import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import CardTable from "../../components/Tables/CardTable";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import AlertsAPI from "../../API/alerts.api";
import Pagination from "../../components/Pagination/Pagination";
import { useHistory } from "react-router-dom";
import MissingData from "../../components/Shared/MissingData";
import { setObject, getObject } from "../../utils/StorageHelpers";
import { longDateTime } from "../../utils/DateHelper";
import ActionsDropdown from "../../components/Dropdown/ActionsDropdown";
import DropdownButton from "../../components/Buttons/DropdownButton";
import { isEmpty } from "lodash";
import {
    FIELD_TYPES,
    NOTIFICATION_TYPES,
    STATUS_OPTIONS,
} from "../../utils/constants";
import CheckBox from "../../components/CheckBox/CheckBox";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import { SuccessAlert } from "../../utils/Alerts";
import LookupsAPI from "../../API/lookups.api";

const Alerts = () => {
    //General states
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const { t } = useTranslation();
    const [isOptionMenuOpened, setIsOptionMenuOpened] = useState(false);

    //Data states
    const [alerts, setAlerts] = useState([]);
    const [selectedAlerts, setSelectedAlerts] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [isCheckboxIndermediate, setIsCheckboxIndermediate] = useState(false);
    const [alertsToBeDeleted, setAlertsToBeDeleted] = useState([]);

    //Modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    //Filter states
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [materialLookup, setMaterialLookup] = useState([]);

    //Sort states
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    //Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Alerts-itemsPerPage", true) || 10
    );
    const [alertsTotalCount, setAlertsTotalCount] = useState(0);

    //Table constants
    const headers = [
        { label: t("name"), name: "name" },
        { label: t("material"), name: "material", skipSort: true },
        {
            label: t("notificationType"),
            name: "notificationType",
            skipSort: true,
        },
        { label: t("createdAt"), name: "createdAt" },
        { label: t("createdBy"), name: "createdBy", skipSort: true },
        { label: t("status"), name: "alertStatus", skipSort: true },
        { label: t("actions"), skipSort: true },
    ];

    const keys = [
        {
            name: "name",
            element: (row, index) => (
                <CheckBox
                    name={row?.id}
                    checked={(selectedAlerts && selectedAlerts[index]) || false}
                    onChangeHandler={() => handleCheckBoxChange(index)}
                    label={row.name}
                    checkBoxClassName="mt-2"
                />
            ),
        },
        { name: "material", element: (row) => row?.material?.label || "---" },
        {
            name: "notificationType",
            element: (row) => renderNotificationTypes(row) || "---",
        },
        {
            name: "createdAt",
            element: (row) =>
                row.createdAt ? longDateTime(row.createdAt) : "---",
        },
        {
            name: "createdBy",
            element: (row) => (
                <div className="flex items-center justify-center">
                    {row?.createdBy?.email}
                </div>
            ),
        },
        {
            name: "alertStatus",
            element: (row) => (
                <div className="flex items-center justify-center">
                    <div
                        className={`tag-ele tag-${
                            row.alertStatus === STATUS_OPTIONS.active
                                ? "completed"
                                : "declined"
                        } px-2`}
                    >
                        {row.alertStatus}
                    </div>
                </div>
            ),
        },
        {
            name: "actions",
            element: (row, index) => (
                <ActionsDropdown
                    attributes={actionsDropdownOptions(row, index)}
                    row={row}
                />
            ),
        },
    ];
    const actionsDropdownOptions = (row, index) => [
        {
            name: t("edit"),
            icon: "fas fa-pen",
            authorized: true,
            handler: () => handleEditAlert(row),
        },
        {
            name:
                row.alertStatus === STATUS_OPTIONS.active
                    ? t("deactivate")
                    : t("activate"),
            icon: `fas fa-${
                row.alertStatus === STATUS_OPTIONS.active
                    ? "bell-slash"
                    : "bell"
            }`,
            authorized: true,
            handler: () => handleAlertStatusUpdate(index, row),
        },
        {
            name: t("delete"),
            icon: "fas fa-trash",
            includeHorizontalLine: true,
            color: "red",
            authorized: true,
            handler: () => handleAlertDelete(index),
        },
    ];

    //Options constants
    const dropdownButtonOptions = [
        {
            icon: "bell",
            label: t("activate"),
            callback: () => handleAlertIdsMapping("activate", selectedAlerts),
        },
        {
            icon: "bell-slash",
            label: t("deactivate"),
            callback: () => handleAlertIdsMapping("deactivate", selectedAlerts),
        },
        {
            icon: "trash-alt",
            label: t("delete"),
            callback: () => {
                setIsDeleteModalOpen(true);
                setAlertsToBeDeleted(selectedAlerts);
            },
        },
    ];

    const filterOptions = [
        {
            label: t("name"),
            name: "name",
            type: FIELD_TYPES.Input,
            placeholder: t("name"),
        },
        {
            label: t("material"),
            name: "materialId",
            type: FIELD_TYPES.Dropdown,
            options: materialLookup,
            placeholder: t("selectPlaceholder", {
                entity: t("material"),
            }),
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: FIELD_TYPES.Date,
            max: getCurrentDateYMD(),
        },
        {
            label: t("status"),
            name: "alertStatus",
            type: FIELD_TYPES.Dropdown,
            options: [
                { label: "Active", value: "Active" },
                { label: "InActive", value: "InActive" },
            ],
            placeholder: t("selectPlaceholder", {
                entity: t("status"),
            }),
        },
    ];

    //Use effects
    useEffect(() => {
        if (sortBy && sortDirection)
            fetchAlerts(filters, 0, sortBy, sortDirection);
        else fetchAlerts(filters);
        setCurrentPage(1);
        getMaterialsLookup();
    }, [sortBy, sortDirection]);

    useEffect(() => {
        setSelectedAlerts(
            alerts?.length ? Array(alerts.length).fill(false) : []
        );
    }, [alerts]);

    useEffect(() => {
        if (!isEmpty(alerts)) {
            const selectedAlertsCount = selectedAlerts.filter(
                (isSelected) => isSelected
            ).length;
            setIsAllSelected(selectedAlertsCount === alerts.length);
            setIsCheckboxIndermediate(
                selectedAlertsCount > 0 && selectedAlertsCount < alerts.length
            );
        }

        if (selectedAlerts.filter((elem) => elem === true).length > 0) {
            setIsOptionMenuOpened(true);
        } else {
            setIsOptionMenuOpened(false);
        }
    }, [selectedAlerts]);

    // Data fetching
    const fetchAlerts = async (
        filters,
        offset = 0,
        defaultSortBy = null,
        defaultSortDirection = null,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        //API call and data setting
        const result = await AlertsAPI.getAlerts(
            itemsPerPage,
            filters,
            offset,
            defaultSortBy,
            defaultSortDirection
        );
        if (result) {
            setAlerts(result.data);
            setAlertsTotalCount(result.totalCount);
        }
        setIsLoading(false);
    };

    const getMaterialsLookup = async () => {
        const result = await LookupsAPI.getMaterials();
        if (result)
            setMaterialLookup(
                result.map((elem) => ({ value: elem.id, label: elem.name }))
            );
    };

    //Handlers
    const handleDeleteAlerts = async (ids) => {
        if (ids && !isEmpty(ids)) {
            setIsDeleting(true);
            const result = await AlertsAPI.deleteAlerts({ ids });
            if (result) {
                setIsDeleteModalOpen(false);
                SuccessAlert(t("alertDeleted"));
                fetchAlerts(filters, 0, sortBy, sortDirection);
            }
        }
        setIsDeleting(false);
    };

    const handleUpdateAlertsStatus = async (ids, alertStatus) => {
        if (ids && !isEmpty(ids)) {
            const result = await AlertsAPI.updateAlertsStatus(
                { ids },
                alertStatus
            );
            if (result) {
                SuccessAlert(t("alertStatusUpdated"));
                fetchAlerts(filters, 0, sortBy, sortDirection);
            }
        }
    };

    const handleEditAlert = (alert) => {
        history.push({
            pathname: "/options/alerts/create",
            state: {
                alertId: alert.id,
            },
        });
    };

    const handleAlertIdsMapping = (action, idsPayload) => {
        if (idsPayload && !isEmpty(idsPayload)) {
            const alertIds = alerts
                .map((elem, index) => {
                    if (idsPayload[index]) return alerts[index]?.id;
                })
                .filter((elem) => elem != null);
            switch (action) {
                case "delete":
                    handleDeleteAlerts(alertIds);
                    break;
                case "activate":
                    handleUpdateAlertsStatus(alertIds, "Active");
                    break;
                case "deactivate":
                    handleUpdateAlertsStatus(alertIds, "InActive");
                    break;
            }
        }
    };

    const handleAlertStatusUpdate = (index, row) => {
        const arr = [];
        arr[index] = true;
        row.alertStatus === STATUS_OPTIONS.active
            ? handleAlertIdsMapping("deactivate", arr)
            : handleAlertIdsMapping("activate", arr);
    };

    const handleAlertDelete = (index) => {
        setIsDeleteModalOpen(true);
        const arr = [];
        arr[index] = true;
        setAlertsToBeDeleted(arr);
    };
    //Pagination functions
    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            fetchAlerts(filters, index - 1, sortBy, sortDirection);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Alerts-itemsPerPage", itemsPerPage, true);
        fetchAlerts(itemsPerPage, filters, 0, sortBy, sortDirection);
    };

    const handleApplyFilters = (usedFilters) => {
        fetchAlerts(usedFilters, 0, sortBy, sortDirection);
    };

    //Handle selected alert insertion and deletion
    const handleCheckBoxChange = (index) => {
        setSelectedAlerts((prevSelected) => {
            const newSelected = [...prevSelected];
            newSelected[index] = !newSelected[index];
            return newSelected;
        });
    };

    //Renderers
    const renderNotificationTypes = (row) => {
        return (
            <div className="flex w-full">
                {row?.notificationConfigurations?.map((elem, index) => (
                    <div
                        className={`${
                            elem?.notificationType === NOTIFICATION_TYPES.email
                                ? "bg-blue-400"
                                : "bg-green-400"
                        } ${
                            row.notificationConfigurations.length === 1
                                ? "rounded-xl w-full"
                                : "w-1/2"
                        } ${
                            index === 0 ? "rounded-l-xl" : "rounded-r-xl"
                        } text-white py-0.5 px-4 ${
                            index !== 0 ? "pl-3" : ""
                        } text-xxs`}
                        key={index}
                    >
                        {elem?.notificationType === NOTIFICATION_TYPES.email
                            ? t("email")
                            : t("system")}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <div id="model-content" className="model-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="alerts">Alerts</Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center gap-2">
                        <FilterDropdown
                            filterOptions={filterOptions}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={handleApplyFilters}
                            component="alerts"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            disabled={isLoading}
                        />
                        <DropdownButton
                            buttonName={`${t("options")}${
                                selectedAlerts?.filter((elem) => elem)?.length
                                    ? ` (${
                                          selectedAlerts.filter((elem) => elem)
                                              .length
                                      })`
                                    : ""
                            }`}
                            options={dropdownButtonOptions}
                            disabled={isEmpty(
                                selectedAlerts?.filter((elem) => elem)
                            )}
                            controlled={true}
                            open={isOptionMenuOpened}
                        />
                        <button
                            className="default-orange-outline-btn"
                            onClick={() => {
                                history.push("/options/alerts/create");
                            }}
                        >
                            <i className="fas fa-plus mr-2" />
                            <Trans i18nKey="newAlert">New Alert</Trans>
                        </button>
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading} text="Loading...">
                <div className="card-table shadow">
                    <div
                        className="tabs-container active"
                        id="tabs-content-all"
                    >
                        {!isEmpty(alerts) ? (
                            <>
                                <CardTable
                                    keys={keys}
                                    headers={headers}
                                    rows={alerts}
                                    purpose={"alerts"}
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                    sortDirection={sortDirection}
                                    setSortDirection={setSortDirection}
                                    setSelectedRows={setSelectedAlerts}
                                    leftAlignFirstColumn={true}
                                    isAllRowsSelected={isAllSelected}
                                    isCheckboxIntermediate={
                                        isCheckboxIndermediate
                                    }
                                    setIsAllRowsSelected={setIsAllSelected}
                                    firstChildClassName="justify-start"
                                />
                                <Pagination
                                    currentPage={currentPage}
                                    setCurrentPage={handlePageClick}
                                    totalCount={alertsTotalCount}
                                    sizePerPage={itemsPerRequest}
                                    handleItemsPerPage={handleItemsPerPage}
                                />
                            </>
                        ) : (
                            <MissingData
                                entity={t("alerts")}
                                path="/options/alerts/create"
                            />
                        )}
                    </div>
                </div>
            </LoadingOverlay>
            <BaseAlertModal
                isVisible={isDeleteModalOpen}
                setIsVisible={setIsDeleteModalOpen}
                iconClassName="fas fa-trash-alt"
                title={t("deleteAlertTitle")}
                message={t("deleteAlertMessage")}
                action={{
                    name: t("delete"),
                    submitAction: () =>
                        handleAlertIdsMapping("delete", alertsToBeDeleted),
                }}
                type="confirmation"
                isSubmitting={isDeleting}
            />
        </>
    );
};

export default Alerts;
